import {
  Badge,
  Button,
  EmptyState,
  PageHeader,
  Skeleton,
  useConfirm,
  useToast,
} from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import { AssignmentHistory } from '@/features/devices/AssignmentHistory';
import { AssignmentPanel } from '@/features/devices/AssignmentPanel';
import { DeviceFiles } from '@/features/devices/DeviceFiles';
import { DeviceForm } from '@/features/devices/DeviceForm';
import { useDeleteDevice, useDevice } from '@/features/devices/hooks';
import { A, useParams } from '@solidjs/router';
import { Show } from 'solid-js';

const STATUS_TONE = {
  available: 'success',
  assigned: 'info',
  lost: 'warning',
  retired: 'neutral',
} as const;

export default function DeviceDetail() {
  const params = useParams<{ deviceId: string }>();
  const query = useDevice(() => params.deviceId);
  const { session } = useAuth();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const remove = useDeleteDevice(params.deviceId);

  const canDelete = () => session()?.permissions.has('devices:delete') ?? false;

  const onRetire = async () => {
    const ok = await confirmDialog({
      title: 'Retire this device?',
      body: 'It will be soft-deleted and removed from inventory.',
      confirmLabel: 'Retire',
      danger: true,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(undefined as never);
      toast.success('Device retired');
    } catch {
      toast.error('Retire failed');
    }
  };

  return (
    <section>
      <PageHeader
        title={
          <Show when={query.data} fallback="…">
            {(data) => (
              <span class="flex flex-wrap items-center gap-2">
                <span class="break-words">{data().data.name}</span>
                <Badge tone={STATUS_TONE[data().data.status]}>{data().data.status}</Badge>
              </span>
            )}
          </Show>
        }
        sub={
          <p class="flex items-center gap-1.5">
            <A href="/devices" class="hover:underline">
              Devices
            </A>
            <span>/</span>
            <Show when={query.data} fallback="…">
              <span>{query.data?.data.name}</span>
            </Show>
          </p>
        }
        actions={
          <Show when={canDelete() && query.data?.data.status !== 'retired'}>
            <Button variant="danger" onClick={onRetire} loading={remove.isPending}>
              Retire
            </Button>
          </Show>
        }
      />

      <div class="mx-auto max-w-6xl p-6">
        <Show when={!query.isPending} fallback={<Skeleton class="h-64" />}>
          <Show
            when={query.data}
            fallback={<EmptyState variant="error" title="Failed to load device" />}
          >
            {(data) => (
              <div class="flex flex-col gap-6 md:flex-row">
                <div class="min-w-0 md:w-3/5">
                  <DeviceForm device={data().data} />
                </div>
                <div class="min-w-0 space-y-4 md:w-2/5">
                  <AssignmentPanel device={data().data} />
                  <DeviceFiles
                    deviceId={data().data.id}
                    files={data().data.files}
                    fileGroups={data().data.fileGroups}
                    assignedUserId={data().data.assignedUserId}
                  />
                  <AssignmentHistory history={data().data.assignmentHistory} />
                </div>
              </div>
            )}
          </Show>
        </Show>
      </div>
    </section>
  );
}
